import React from 'react';
import { Geography } from 'react-simple-maps';
import { MAX_OPACITY } from './constants/LocationConstants';
import constants from '@shared-styles/constants.module.scss';
import { hexToRgbWithAlpha } from '../../../../../utils/colorExtentions';

const DEFAULT_COLOR = constants.organizationColor;
const EMPTY_COLOR = constants.whiteColor;
const BORDER_COLOR = constants.borderColor;

/**
 * @typedef {object} LocationGeographyLocationDataOrgUnits
 * @property {string} name
 * @property {number} fei
 */

/**
 * @typedef {object} LocationGeographyLocationData
 * @property {number} [rank]
 * @property {LocationGeographyLocationDataOrgUnits[]} [orgUnits]
 */

/**
 * @typedef {object} LocationGeographyTooltipRefCurrent
 * @property {(update: { name: string, locationData?: LocationGeographyLocationData }) => void} [update]
 * @property {() => void} [hide]
 * @property {(clientY: number, clientX: number) => void} [setPosition]
 */

/**
 * @typedef {object} LocationGeographyTooltipRef
 * @property {LocationGeographyTooltipRefCurrent} [current]
 */

/**
 * @typedef {object} LocationGeographyProps
 * @property {LocationGeographyLocationData} [locationData]
 * @property {number} [opacityPerRank]
 * @property {Object} geography
 * @property {LocationGeographyTooltipRef} [tooltipRef]
 */

/**
 * @param {LocationGeographyProps} props
 */
const LocationGeography = props => {
  const { locationData, opacityPerRank, geography, tooltipRef } = props;

  const handleMouseOver = () => {
    tooltipRef.current.update({
      name: geography.properties.name,
      locationData
    });
  };

  const handleMouseMove = event => {
    const { clientX, clientY } = event;
    tooltipRef.current.setPosition(clientY, clientX);
  };

  const handleMouseLeave = () => {
    tooltipRef.current.hide();
  };

  const fillColor = getFillColor(locationData, opacityPerRank);

  return (
    <Geography
      data-testid={geography.rsmKey}
      geography={geography}
      stroke={BORDER_COLOR}
      strokeWidth={0.5}
      fill={fillColor}
      onMouseOver={handleMouseOver}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        default: { outline: 'none' },
        pressed: { outline: 'none' },
        hover: { outline: 'none' }
      }}
    />
  );
};

const getFillColor = (locationData, opacityPerRank) => {
  if (!locationData) {
    return EMPTY_COLOR;
  }

  const opacity = MAX_OPACITY - locationData.rank * opacityPerRank;

  return hexToRgbWithAlpha(DEFAULT_COLOR, opacity);
};

export default LocationGeography;
